<template>
  <div class="row g-3">
    <div class="col-12">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h4 class="font-serif mb-0">
          {{ $t("dashboard.portfolio.guarantees_of_origin_in_portfolios") }}
        </h4>
        <div
          v-if="showHknrButtons && hknrAccountId !== undefined"
          class="d-flex align-items-center gap-2"
        >
          <TsoDeclarationExcelButton :accountId="hknrAccountId" />
          <BulkIssuanceButton />
        </div>
      </div>
      <TabsContainer
        :tabs="inventoryTabs"
        @change:tab="handleTabChange"
        :activeTab="activeInventoryTab"
      >
        <template #tab-overview>
          <FilterablePortfolioChartPanel />
        </template>
        <template #tab-receive>
          <ReceiveGoComponent :company="props.company" />
        </template>
        <template #tab-portfolios>
          <PortfoliosListComponent />
        </template>
        <template #tab-send>
          <SendGoComponent :company="props.company" />
        </template>
        <template #tab-buy>
          <BuyGoComponent
            :company-id="props.companyId"
            :company="props.company"
          />
        </template>
        <template #tab-sell>
          <SellGoComponent :company="props.company" />
        </template>
        <template #tab-cancel>
          <CancelGoComponent
            :company-id="props.companyId"
            :company="props.company"
          />
        </template>
        <template #tab-orders>
          <CompanyOrdersComponent
            :companies="availableCompanies"
            :is_admin="props.is_admin || false"
          />
        </template>
      </TabsContainer>
    </div>
  </div>
</template>

<script setup lang="ts">
import TabsContainer from "@/components/common/tabs/TabsContainer.vue";
import { useAccountFeatureFlags } from "@/composables/useAccountFeatureFlags";
import { useConsolidated } from "@/composables/useConsolidated";
import { useTabs } from "@/composables/useTabs";
import ReceiveGoComponent from "@/dashboard/inventory_management/accounts/ReceiveGoComponent.vue";
import BuyGoComponent from "@/dashboard/inventory_management/buy/BuyGoComponent.vue";
import CompanyOrdersComponent from "@/dashboard/inventory_management/company_orders/CompanyOrdersComponent.vue";
import PortfoliosListComponent from "@/dashboard/inventory_management/portfolios/PortfoliosListComponent.vue";
import BulkIssuanceButton from "@/registry/hknr/components/BulkIssuanceButton.vue";
import TsoDeclarationExcelButton from "@/registry/hknr/components/TsoDeclarationExcelButton.vue";
import type { Account, Company, FeatureFlags } from "@/types";
import { computed, provide, ref, toRef } from "vue";
import { useI18n } from "vue-i18n";
import CancelGoComponent from "./cancel/CancelGoComponent.vue";
import FilterablePortfolioChartPanel from "./overview/FilterablePortfolioChartPanel.vue";
import SellGoComponent from "./sell/SellGoComponent.vue";
import SendGoComponent from "./send/SendGoComponent.vue";

const props = defineProps<{
  companyId: number;
  company: Company;
  companies?: Company[];
  is_admin?: boolean;
  account?: Account;
}>();

const { t } = useI18n();
const { consolidated } = useConsolidated();
const { hasFlag } = useAccountFeatureFlags(toRef(props, "account"));

const showHknrButtons = computed(
  () => consolidated.value && hasFlag("hknr_bulk_issuance_report_enabled").value
);
const hknrAccountId = computed(() => props.account?.id);

const availableCompanies = computed(() => {
  return props.companies || [props.company];
});

const inventoryTabs = computed(() => {
  const featureFlags: FeatureFlags = props.company?.feature_flags || {};
  const hasMultipleCompanies = availableCompanies.value.length > 1;

  // For multiple companies, only show overview and orders tabs
  if (hasMultipleCompanies) {
    return [
      {
        id: "overview",
        name: t("tradingCenter.tabs.overview"),
        icon: "chart-pie",
        disabled: false,
      },
      {
        id: "orders",
        name: t("company_orders.title"),
        icon: "list-ul",
        disabled: false,
      },
    ];
  }

  // For single company, show all tabs based on feature flags
  const allTabs = [
    {
      id: "overview",
      name: t("tradingCenter.tabs.overview"),
      icon: "chart-pie",
      disabled: false,
    },
    {
      id: "send",
      name: t("common.actions.send"),
      icon: "paper-plane",
      disabled: false,
    },
    {
      id: "receive",
      name: t("common.actions.receive"),
      icon: "inbox",
      disabled: false,
    },
    {
      id: "portfolios",
      name: t("common.actions.portfolios"),
      icon: "layer-group",
      disabled: false,
    },
    {
      id: "buy",
      name: t("common.actions.buy"),
      icon: "shopping-cart",
      disabled: false,
    },
    {
      id: "cancel",
      name: t("common.actions.cancel"),
      icon: "ban",
      disabled: false,
    },
    {
      id: "sell",
      name: t("common.actions.sell"),
      icon: "dollar-sign",
      disabled: !featureFlags.hold_allocation,
    },
    {
      id: "orders",
      name: t("company_orders.title"),
      icon: "list-ul",
      disabled: false,
    },
  ];

  return allTabs.filter((tab) => !tab.disabled);
});

const { activeTab, setTab: handleTabChange } = useTabs("certificates-history", {
  defaultTabId: "overview",
  tabs: inventoryTabs,
});

const activeInventoryTab = computed(() => activeTab.value || "overview");

provide("navigateToInventoryTab", handleTabChange);

const prefillBuyVolume = ref<number | null>(null);
provide("prefillBuyVolume", prefillBuyVolume);
</script>
